import { MetricUnit } from '@/__generated__/graphql';
import { type I18n } from '@lingui/core';
import { t } from '@lingui/macro';

// --- Type Definitions ---

export type FlowUnit =
  | MetricUnit.LiterPerSecond
  | MetricUnit.CubicMeterPerSecond
  | MetricUnit.CubicMeterPerHour
  | MetricUnit.GallonPerSecond
  | MetricUnit.GallonPerMinute
  | MetricUnit.GallonPerHour
  | MetricUnit.GallonPerDay
  | MetricUnit.CubicFootPerMinute
  | MetricUnit.CubicFootPerSecond
  | MetricUnit.AcreFootPerSecond;

export type DistanceUnit =
  | MetricUnit.Meter
  | MetricUnit.Centimeter
  | MetricUnit.Millimeter
  | MetricUnit.Inch
  | MetricUnit.Foot;

export type VelocityUnit =
  | MetricUnit.MillimeterPerSecond
  | MetricUnit.CentimeterPerSecond
  | MetricUnit.FootPerSecond;

export type VolumeUnit =
  | MetricUnit.Liter
  | MetricUnit.CubicMeter
  | MetricUnit.CubicHectometer
  | MetricUnit.Gallon
  | MetricUnit.CubicFoot
  | MetricUnit.AcreFoot
  | MetricUnit.AcreInch;

export type MassUnit = MetricUnit.Kilogram | MetricUnit.Pound;

export type TemperatureUnit = MetricUnit.Celsius | MetricUnit.Fahrenheit;
export type AngleUnit = MetricUnit.Degree | MetricUnit.Radian;
export type ElectricPotentialUnit = MetricUnit.Volt | MetricUnit.Millivolt;
export type ElectricCurrentUnit = MetricUnit.Ampere;
export type ElectricPowerUnit = MetricUnit.Watt;
export type PhUnit = MetricUnit.Ph;
export type ConductivityUnit =
  | MetricUnit.MicrosiemensPerCentimeter
  | MetricUnit.MillisiemensPerCentimeter;
export type TurbidityUnit = MetricUnit.Ntu | MetricUnit.Fnu;
export type PercentUnit = MetricUnit.Percent;

export type AllocationUnit =
  | MetricUnit.LiterPerSecondPerAction
  | MetricUnit.CubicMeterPerSecondPerAction
  | MetricUnit.GallonPerMinutePerAction
  | MetricUnit.CubicFootPerSecondPerAction;

// --- Resolver Function ---

export const getUnits = (i18n: I18n) => {
  const units: Record<
    Exclude<MetricUnit, MetricUnit.None>,
    { value: string; short: string; long: string }
  > = {
    // --- Flow ---
    [MetricUnit.LiterPerSecond]: {
      value: 'liter-per-second',
      short: t(i18n)`L/s`,
      long: t(i18n)`litro por segundo`,
    },
    [MetricUnit.CubicMeterPerSecond]: {
      value: 'cubic-meter-per-second',
      short: t(i18n)`m³/s`,
      long: t(i18n)`metro cúbico por segundo`,
    },
    [MetricUnit.CubicMeterPerHour]: {
      value: 'cubic-meter-per-hour',
      short: t(i18n)`m³/h`,
      long: t(i18n)`metro cúbico por hora`,
    },
    [MetricUnit.GallonPerSecond]: {
      value: 'gallon-per-second',
      short: t(i18n)`gal/s`,
      long: t(i18n)`galón por segundo`,
    },
    [MetricUnit.GallonPerMinute]: {
      value: 'gallon-per-minute',
      short: t(i18n)`GPM`,
      long: t(i18n)`galón por minuto`,
    },
    [MetricUnit.GallonPerHour]: {
      value: 'gallon-per-hour',
      short: t(i18n)`GPH`,
      long: t(i18n)`galón por hora`,
    },
    [MetricUnit.GallonPerDay]: {
      value: 'gallon-per-day',
      short: t(i18n)`GPD`,
      long: t(i18n)`galón por día`,
    },
    [MetricUnit.CubicFootPerMinute]: {
      value: 'cubic-foot-per-minute',
      short: t(i18n)`CFM`,
      long: t(i18n)`pie cúbico por minuto`,
    },
    [MetricUnit.CubicFootPerSecond]: {
      value: 'cubic-foot-per-second',
      short: t(i18n)`CFS`,
      long: t(i18n)`pie cúbico por segundo`,
    },
    [MetricUnit.AcreFootPerSecond]: {
      value: 'acre-foot-per-second',
      short: t(i18n)`ac-ft/s`,
      long: t(i18n)`acre-pie por segundo`,
    },

    // --- Distance ---
    [MetricUnit.Meter]: {
      value: 'meter',
      short: t(i18n)`m`,
      long: t(i18n)`metro`,
    },
    [MetricUnit.Centimeter]: {
      value: 'centimeter',
      short: t(i18n)`cm`,
      long: t(i18n)`centímetro`,
    },
    [MetricUnit.Millimeter]: {
      value: 'millimeter',
      short: t(i18n)`mm`,
      long: t(i18n)`milímetro`,
    },
    [MetricUnit.Inch]: {
      value: 'inch',
      short: t(i18n)`in`,
      long: t(i18n)`pulgada`,
    },
    [MetricUnit.Foot]: {
      value: 'foot',
      short: t(i18n)`ft`,
      long: t(i18n)`pie`,
    },

    // --- Volume ---
    [MetricUnit.Liter]: {
      value: 'liter',
      short: t(i18n)`L`,
      long: t(i18n)`litro`,
    },
    [MetricUnit.CubicMeter]: {
      value: 'cubic-meter',
      short: t(i18n)`m³`,
      long: t(i18n)`metro cúbico`,
    },
    [MetricUnit.CubicHectometer]: {
      value: 'cubic-hectometer',
      short: t(i18n)`hm³`,
      long: t(i18n)`hectómetro cúbico`,
    },
    [MetricUnit.Gallon]: {
      value: 'gallon',
      short: t(i18n)`gal`,
      long: t(i18n)`galón`,
    },
    [MetricUnit.CubicFoot]: {
      value: 'cubic-foot',
      short: t(i18n)`ft³`,
      long: t(i18n)`pie cúbico`,
    },
    [MetricUnit.AcreFoot]: {
      value: 'acre-foot',
      short: t(i18n)`ac-ft`,
      long: t(i18n)`acre-pie`,
    },
    [MetricUnit.AcreInch]: {
      value: 'acre-inch',
      short: t(i18n)`ac-in`,
      long: t(i18n)`acre-pulgada`,
    },

    // --- Velocity ---
    [MetricUnit.MillimeterPerSecond]: {
      value: 'millimeter-per-second',
      short: t(i18n)`mm/s`,
      long: t(i18n)`milímetro por segundo`,
    },
    [MetricUnit.CentimeterPerSecond]: {
      value: 'centimeter-per-second',
      short: t(i18n)`cm/s`,
      long: t(i18n)`centímetro por segundo`,
    },
    [MetricUnit.FootPerSecond]: {
      value: 'foot-per-second',
      short: t(i18n)`ft/s`,
      long: t(i18n)`pie por segundo`,
    },

    // --- Mass ---
    [MetricUnit.Kilogram]: {
      value: 'kilogram',
      short: t(i18n)`kg`,
      long: t(i18n)`kilogramo`,
    },
    [MetricUnit.Pound]: {
      value: 'pound',
      short: t(i18n)`lb`,
      long: t(i18n)`libra`,
    },

    // --- Allocation ---
    [MetricUnit.LiterPerSecondPerAction]: {
      value: 'liter-per-second-per-action',
      short: t(i18n)`L/s/acc`,
      long: t(i18n)`litro por segundo por acción`,
    },
    [MetricUnit.CubicMeterPerSecondPerAction]: {
      value: 'cubic-meter-per-second-per-action',
      short: t(i18n)`m³/s/acc`,
      long: t(i18n)`metro cúbico por segundo por acción`,
    },
    [MetricUnit.GallonPerMinutePerAction]: {
      value: 'gallon-per-minute-per-action',
      short: t(i18n)`GPM/acc`,
      long: t(i18n)`galón por minuto por acción`,
    },
    [MetricUnit.CubicFootPerSecondPerAction]: {
      value: 'cubic-foot-per-second-per-action',
      short: t(i18n)`CFS/acc`,
      long: t(i18n)`pie cúbico por segundo por acción`,
    },

    // --- Others ---
    [MetricUnit.Degree]: {
      value: 'degree',
      short: t(i18n)`°`,
      long: t(i18n)`grado`,
    },
    [MetricUnit.Radian]: {
      value: 'radian',
      short: t(i18n)`rad`,
      long: t(i18n)`radián`,
    },
    [MetricUnit.Percent]: {
      value: 'percent',
      short: t(i18n)`%`,
      long: t(i18n)`porcentaje`,
    },
    [MetricUnit.Ampere]: {
      value: 'ampere',
      short: t(i18n)`A`,
      long: t(i18n)`amperio`,
    },
    [MetricUnit.Watt]: {
      value: 'watt',
      short: t(i18n)`W`,
      long: t(i18n)`vatio`,
    },
    [MetricUnit.WattPerSquareMeter]: {
      value: 'watt-per-square-meter',
      short: t(i18n)`W/m²`,
      long: t(i18n)`vatio por metro cuadrado`,
    },
    [MetricUnit.Volt]: {
      value: 'volt',
      short: t(i18n)`V`,
      long: t(i18n)`voltio`,
    },
    [MetricUnit.Ph]: {
      value: 'ph',
      short: t(i18n)`pH`,
      long: t(i18n)`ph`,
    },
    [MetricUnit.Millivolt]: {
      value: 'millivolt',
      short: t(i18n)`mV`,
      long: t(i18n)`milivoltio`,
    },
    [MetricUnit.Ntu]: {
      value: 'ntu',
      short: t(i18n)`NTU`,
      long: t(i18n)`ntu`,
    },
    [MetricUnit.Fnu]: {
      value: 'fnu',
      short: t(i18n)`FNU`,
      long: t(i18n)`fnu`,
    },
    [MetricUnit.Celsius]: {
      value: 'celsius',
      short: t(i18n)`°C`,
      long: t(i18n)`celsius`,
    },
    [MetricUnit.Fahrenheit]: {
      value: 'fahrenheit',
      short: t(i18n)`°F`,
      long: t(i18n)`fahrenheit`,
    },
    [MetricUnit.MillisiemensPerCentimeter]: {
      value: 'millisiemens-per-centimeter',
      short: t(i18n)`mS/cm`,
      long: t(i18n)`milisiemens por centímetro`,
    },
    [MetricUnit.MicrosiemensPerCentimeter]: {
      value: 'microsiemens-per-centimeter',
      short: t(i18n)`µS/cm`,
      long: t(i18n)`microsiemens por centímetro`,
    },
    [MetricUnit.MilligramPerLiter]: {
      value: 'milligram-per-liter',
      short: t(i18n)`mg/L`,
      long: t(i18n)`miligramo por litro`,
    },
    [MetricUnit.Millisecond]: {
      value: 'millisecond',
      short: t(i18n)`ms`,
      long: t(i18n)`milisegundo`,
    },
    [MetricUnit.Second]: {
      value: 'second',
      short: t(i18n)`s`,
      long: t(i18n)`segundo`,
    },
  };

  return units;
};

export const getUnit = (unit: MetricUnit, i18n: I18n) => {
  if (unit === MetricUnit.None) return null;
  const units = getUnits(i18n);
  return units[unit];
};
